import { useState, useEffect } from 'react';
import { Modal, Button } from '@/components/ui';

export interface BarFilters {
  servesBrunch?: boolean;
  goodForWatchingSports?: boolean;
  allowsDogs?: boolean;
  outdoorSeating?: boolean;
  servesBeer?: boolean;
  servesWine?: boolean;
  servesCocktails?: boolean;
}

interface BarFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: BarFilters;
  onApply: (filters: BarFilters) => void;
}

interface FilterOption {
  key: keyof BarFilters;
  label: string;
}

const featureFilters: FilterOption[] = [
  { key: 'servesBrunch', label: 'Brunch' },
  { key: 'goodForWatchingSports', label: 'Sports' },
  { key: 'allowsDogs', label: 'Dog Friendly' },
  { key: 'outdoorSeating', label: 'Patio' },
];

const drinkFilters: FilterOption[] = [
  { key: 'servesBeer', label: 'Beer' },
  { key: 'servesWine', label: 'Wine' },
  { key: 'servesCocktails', label: 'Cocktails' },
];

export function BarFilterModal({ isOpen, onClose, filters, onApply }: BarFilterModalProps) {
  const [localFilters, setLocalFilters] = useState<BarFilters>(filters);

  // Sync local state when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalFilters(filters);
    }
  }, [isOpen, filters]);

  const toggleFilter = (key: keyof BarFilters) => {
    setLocalFilters((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const clearAll = () => {
    setLocalFilters({});
  };

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const activeCount = Object.values(localFilters).filter(Boolean).length;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Filter Bars">
      <div className="space-y-6">
        {/* Features section */}
        <div>
          <h3 className="mb-3 text-sm font-medium text-[var(--color-muted)]">Features</h3>
          <div className="space-y-2">
            {featureFilters.map(({ key, label }) => (
              <label
                key={key}
                className="flex cursor-pointer items-center gap-3 rounded-lg p-2 hover:bg-[var(--color-border)]"
              >
                <input
                  type="checkbox"
                  checked={!!localFilters[key]}
                  onChange={() => toggleFilter(key)}
                  className="h-4 w-4 rounded border-[var(--color-border)] bg-transparent text-accent-primary focus:ring-accent-primary"
                />
                <span className="text-sm">{label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Drinks section */}
        <div>
          <h3 className="mb-3 text-sm font-medium text-[var(--color-muted)]">Drinks</h3>
          <div className="space-y-2">
            {drinkFilters.map(({ key, label }) => (
              <label
                key={key}
                className="flex cursor-pointer items-center gap-3 rounded-lg p-2 hover:bg-[var(--color-border)]"
              >
                <input
                  type="checkbox"
                  checked={!!localFilters[key]}
                  onChange={() => toggleFilter(key)}
                  className="h-4 w-4 rounded border-[var(--color-border)] bg-transparent text-accent-primary focus:ring-accent-primary"
                />
                <span className="text-sm">{label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between border-t border-[var(--color-border)] pt-4">
          <Button variant="ghost" size="sm" onClick={clearAll} disabled={activeCount === 0}>
            Clear All
          </Button>
          <Button onClick={handleApply}>
            Apply{activeCount > 0 && ` (${activeCount})`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
